import { ResourceType } from '@cotera/api';
import { resourceIdFromArtifactPayload } from './artifacts';
import type {
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { AgentModel } from '@cotera/client/app/v1/agent/agent.model';
import { DocumentModel } from '@cotera/client/app/v1/document/document.model';
import { WorkflowModel } from '@cotera/client/app/v1/workflow/workflow.model';
import { CocoChatModel } from '@cotera/client/app/v1/chat/models/coco-chat.model';
import { getArtifactFallbackLabel } from './use-artifact-title';
import type { ArtifactDescriptor } from './artifacts';

export type DirtyArtifactEntry = {
  artifactId: string;
  type: string;
  title: string;
  resourceType: ResourceType;
  resourceId: string;
};

type SavableArtifactType = 'agent' | 'document' | 'workflow';

type DirtyCollectorContext = Pick<ApplicableContext, 'getInScopeModelsOfType'>;

function titleOrFallback(
  title: string | null | undefined,
  type: SavableArtifactType,
  resourceId: string
): string {
  const trimmed = title?.trim();
  if (trimmed !== undefined && trimmed !== '') {
    return trimmed;
  }
  // No id. Everything reaching here is an in-scope artifact with unsaved changes
  // — new or being edited, never unresolvable — so the id disambiguates nothing
  // and an id appended in a save prompt reads like a fault.
  return getArtifactFallbackLabel(
    { type, artifactId: resourceId },
    { includeId: false }
  );
}

/**
 * Whether the chat-level Save Artifacts orchestrator should handle Ctrl+S.
 * Sub-save actions ({@link SaveAgentAction}, etc.) return false from
 * `applicable` when this is true so they do not also bind the shortcut.
 */
export function isSaveArtifactsApplicable(context: ApplicableContext): boolean {
  if (context.hasTargetedModelOfType(CocoChatModel) !== true) {
    return false;
  }
  return collectDirtySavableArtifacts(context).length > 0;
}

/**
 * Collects dirty savable artifact models currently in the actions scope.
 * Dedupes workflows by workflowId (canvas + workflow-node can both mount one).
 */
export function collectDirtySavableArtifacts(
  context: DirtyCollectorContext
): DirtyArtifactEntry[] {
  const entries: DirtyArtifactEntry[] = [];
  const seen = new Set<string>();

  const agents = context.getInScopeModelsOfType(AgentModel);
  for (const agent of agents) {
    if (agent.hasUnsavedChangesWatchable.snapshot() !== true) {
      continue;
    }
    const key = `${ResourceType.Agent}:${agent.agentId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    entries.push({
      artifactId: agent.agentId,
      type: 'agent',
      title: titleOrFallback(
        agent.titleWatchable.snapshot(),
        'agent',
        agent.agentId
      ),
      resourceType: ResourceType.Agent,
      resourceId: agent.agentId,
    });
  }

  const documents = context.getInScopeModelsOfType(DocumentModel);
  for (const document of documents) {
    if (document.hasUnsavedChangesWatchable.snapshot() !== true) {
      continue;
    }
    const key = `${ResourceType.Document}:${document.documentId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    entries.push({
      artifactId: document.documentId,
      type: 'document',
      title: titleOrFallback(
        document.titleWatchable.snapshot(),
        'document',
        document.documentId
      ),
      resourceType: ResourceType.Document,
      resourceId: document.documentId,
    });
  }

  const workflows = context.getInScopeModelsOfType(WorkflowModel);
  for (const workflow of workflows) {
    if (workflow.hasUnsavedChangesWatchable.snapshot() !== true) {
      continue;
    }
    const key = `${ResourceType.Workflow}:${workflow.workflowId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    entries.push({
      artifactId: workflow.workflowId,
      type: 'workflow',
      title: titleOrFallback(
        workflow.nameWatchable.snapshot(),
        'workflow',
        workflow.workflowId
      ),
      resourceType: ResourceType.Workflow,
      resourceId: workflow.workflowId,
    });
  }

  return entries;
}

/**
 * Resource ids (agent/document/workflow) that currently have unsaved changes.
 * Used by the artifact picker to show dirty dots without full entry metadata.
 */
export function collectDirtyResourceIds(
  context: DirtyCollectorContext
): Set<string> {
  return new Set(
    collectDirtySavableArtifacts(context).map((entry) => entry.resourceId)
  );
}

/** Artifact types with an editable, savable resource behind them. */
const SAVABLE_ARTIFACT_TYPES: ReadonlySet<string> = new Set([
  ResourceType.Agent,
  ResourceType.Document,
  ResourceType.Workflow,
]);

export function artifactMatchesDirtyResource(
  artifact: Pick<ArtifactDescriptor, 'type' | 'artifactId'> & {
    payload?: unknown;
  },
  dirtyResourceIds: ReadonlySet<string>
): boolean {
  // A workflow step is a sub-view of its workflow rather than a resource, so it
  // is dirty when that workflow is — and has no id of its own to fall back to.
  if (artifact.type === 'workflow-node') {
    const workflowId = (artifact.payload as { workflowId?: string } | null)
      ?.workflowId;
    return workflowId !== undefined && dirtyResourceIds.has(workflowId);
  }
  if (!SAVABLE_ARTIFACT_TYPES.has(artifact.type)) {
    return false;
  }
  const resourceId = resourceIdFromArtifactPayload(
    artifact.type,
    artifact.payload,
    artifact.artifactId
  );
  return resourceId !== null && dirtyResourceIds.has(resourceId);
}

/** Resolve the in-scope savable model for a dirty entry so save can target it. */
export function resolveSavableModelForDirtyEntry(
  context: ExecuteContext,
  entry: DirtyArtifactEntry
): AgentModel | DocumentModel | WorkflowModel | null {
  switch (entry.resourceType) {
    case ResourceType.Agent: {
      const models = context.getInScopeModelsOfType(AgentModel);
      return models.find((m) => m.agentId === entry.resourceId) ?? null;
    }
    case ResourceType.Document: {
      const models = context.getInScopeModelsOfType(DocumentModel);
      return models.find((m) => m.documentId === entry.resourceId) ?? null;
    }
    case ResourceType.Workflow: {
      const models = context.getInScopeModelsOfType(WorkflowModel);
      return models.find((m) => m.workflowId === entry.resourceId) ?? null;
    }
    default:
      return null;
  }
}
